import { type MedicationIncidentSubtype } from './incident-taxonomy';
export const MEDICATION_EVENT_FIELDS = [
  'medicine',
  'dose',
  'strength',
  'formulation',
  'route',
  'frequency',
] as const;

type MedicationEventFacts = Partial<Record<(typeof MEDICATION_EVENT_FIELDS)[number], string>>;
export type MedicationEventContext = {
  entry_id: string;
  source: 'MANUAL' | 'EMAR_SNAPSHOT';
  administration_id?: string;
  order_id?: string;
  expected: MedicationEventFacts;
  actual: MedicationEventFacts;
  stage?: 'PRESCRIBING' | 'DISPENSING' | 'ADMINISTRATION' | 'MONITORING';
  scheduled_time?: string;
  actual_time?: string;
  staff_shift?: string;
  witness?: string;
  source_client_id?: string;
  advice_sought?: string;
  advice_given?: string;
  correction_of_id?: string;
  correction_reason?: string;
};

type Path = (string | number)[];
type Issue = { code: string; path: Path; message: string; [key: string]: unknown };
type Validation = { issues: Issue[]; aborted: boolean };
type Validator<T> = (value: unknown, path: Path, validation: Validation) => T;

class MedicationContextValidationError extends Error {
  constructor(readonly issues: Issue[]) {
    super(JSON.stringify(issues, null, 2));
    this.name = 'MedicationContextValidationError';
  }
  get errors() {
    return this.issues;
  }
}

function schema<T>(validate: Validator<T>) {
  const safeParse = (value: unknown) => {
    const validation: Validation = { issues: [], aborted: false };
    const data = validate(value, [], validation);
    return validation.issues.length
      ? ({
          success: false,
          error: new MedicationContextValidationError(validation.issues),
        } as const)
      : ({ success: true, data } as const);
  };
  return {
    safeParse,
    parse(value: unknown): T {
      const result = safeParse(value);
      if (!result.success) throw result.error;
      return result.data;
    },
  };
}

function parsedType(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'number' && Number.isNaN(value)) return 'nan';
  if (value instanceof Date) return 'date';
  if (value instanceof Map) return 'map';
  if (value instanceof Set) return 'set';
  if (
    typeof value === 'object' &&
    typeof (value as { then?: unknown }).then === 'function' &&
    typeof (value as { catch?: unknown }).catch === 'function'
  )
    return 'promise';
  return typeof value;
}

function invalidType(value: unknown, expected: string, path: Path, validation: Validation) {
  const received = parsedType(value);
  validation.aborted = true;
  validation.issues.push({
    code: 'invalid_type',
    expected,
    received,
    path,
    message: received === 'undefined' ? 'Required' : `Expected ${expected}, received ${received}`,
  });
}

type Shape = Record<string, Validator<unknown>>;
function object(value: unknown, shape: Shape, path: Path, validation: Validation) {
  if (parsedType(value) !== 'object') {
    invalidType(value, 'object', path, validation);
    return {};
  }
  const input = value as Record<string, unknown>;
  const output: Record<string, unknown> = {};
  for (const [key, validate] of Object.entries(shape)) {
    const result = validate(input[key], [...path, key], validation);
    if (result !== undefined || key in input) output[key] = result;
  }
  // Match strict object validation, including inherited enumerable unknown keys.
  const keys: string[] = [];
  for (const key in input) if (!Object.hasOwn(shape, key)) keys.push(key);
  if (keys.length)
    validation.issues.push({
      code: 'unrecognized_keys',
      keys,
      path,
      message: `Unrecognized key(s) in object: ${keys.map((key) => `'${key}'`).join(', ')}`,
    });
  return output;
}

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Calendar-valid dates, optional seconds/fractions and Z or numeric offsets.
// Offset digits deliberately have no range restriction, matching the previous validator.
const date = String.raw`((\d\d[2468][048]|\d\d[13579][26]|\d\d0[48]|[02468][048]00|[13579][26]00)-02-29|\d{4}-((0[13578]|1[02])-(0[1-9]|[12]\d|3[01])|(0[469]|11)-(0[1-9]|[12]\d|30)|(02)-(0[1-9]|1\d|2[0-8])))`;
const datetime = new RegExp(
  String.raw`^${date}T([01]\d|2[0-3]):[0-5]\d(:[0-5]\d(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$`,
);

function string(
  kind: 'fact' | 'uuid' | 'datetime',
  optional = true,
): Validator<string | undefined> {
  return (value, path, validation) => {
    if (optional && value === undefined) return undefined;
    if (typeof value !== 'string') {
      invalidType(value, 'string', path, validation);
      return undefined;
    }
    if (kind === 'fact' && value.length > 1000)
      validation.issues.push({
        code: 'too_big',
        maximum: 1000,
        type: 'string',
        inclusive: true,
        exact: false,
        path,
        message: 'String must contain at most 1000 character(s)',
      });
    if (kind !== 'fact' && !(kind === 'uuid' ? uuid : datetime).test(value))
      validation.issues.push({
        code: 'invalid_string',
        validation: kind,
        path,
        message: kind === 'uuid' ? 'Invalid uuid' : 'Invalid datetime',
      });
    return value;
  };
}

function enumeration<T extends string>(
  options: readonly T[],
  optional = false,
): Validator<T | undefined> {
  return (value, path, validation) => {
    if (optional && value === undefined) return undefined;
    const expected = options.map((option) => `'${option}'`).join(' | ');
    if (typeof value !== 'string') {
      invalidType(value, expected, path, validation);
      return undefined;
    }
    if (!options.includes(value as T)) {
      validation.aborted = true;
      validation.issues.push({
        code: 'invalid_enum_value',
        received: value,
        options: [...options],
        path,
        message: `Invalid enum value. Expected ${expected}, received '${value}'`,
      });
    }
    return value as T;
  };
}

const facts: Validator<MedicationEventFacts> = (value, path, validation) =>
  object(
    value,
    {
      medicine: string('fact'),
      dose: string('fact'),
      strength: string('fact'),
      formulation: string('fact'),
      route: string('fact'),
      frequency: string('fact'),
    },
    path,
    validation,
  );

/** Parse strict medication facts without normalising immutable string bytes. */
export function medicationEventFactsSchema(value: unknown): MedicationEventFacts {
  return schema(facts).parse(value);
}

const context: Validator<MedicationEventContext> = (value, path, validation) => {
  // Refinements run for size/format/unknown-key failures, but not invalid types/enums.
  const local: Validation = { issues: validation.issues, aborted: false };
  const result = object(
    value,
    {
      entry_id: string('uuid', false),
      source: enumeration(['MANUAL', 'EMAR_SNAPSHOT']),
      administration_id: string('uuid'),
      order_id: string('uuid'),
      expected: facts,
      actual: facts,
      stage: enumeration(['PRESCRIBING', 'DISPENSING', 'ADMINISTRATION', 'MONITORING'], true),
      scheduled_time: string('datetime'),
      actual_time: string('datetime'),
      staff_shift: string('fact'),
      witness: string('fact'),
      source_client_id: string('uuid'),
      advice_sought: string('fact'),
      advice_given: string('fact'),
      correction_of_id: string('uuid'),
      correction_reason: string('fact'),
    },
    path,
    local,
  ) as MedicationEventContext;
  if (local.aborted) validation.aborted = true;
  else {
    const issue = (field: string, message: string) =>
      validation.issues.push({
        code: 'custom',
        message,
        path: [...path, field],
      });
    if (result.source === 'EMAR_SNAPSHOT' && !result.administration_id && !result.order_id)
      issue('source', 'Choose an eMAR record.');
    if (result.source === 'MANUAL' && (result.administration_id || result.order_id))
      issue('source', 'Manual facts cannot claim an eMAR source.');
    if (result.correction_of_id && !result.correction_reason?.trim())
      issue('correction_reason', 'Explain the correction.');
  }
  return result;
};

export const medicationEventContextSchema = schema(context);
export const medicationEventContextsSchema = schema<MedicationEventContext[]>(
  (value, path, validation) => {
    if (!Array.isArray(value)) {
      invalidType(value, 'array', path, validation);
      return [];
    }
    if (value.length > 30)
      validation.issues.push({
        code: 'too_big',
        maximum: 30,
        type: 'array',
        inclusive: true,
        exact: false,
        path,
        message: 'Array must contain at most 30 element(s)',
      });
    return Array.from(value, (entry, index) => context(entry, [...path, index], validation));
  },
);

/** Optional enrichment is a separate transaction: it can never roll back reporting. */
export async function persistOptionalMedicationContext(
  save: () => Promise<{ error?: unknown }>,
): Promise<boolean> {
  try {
    return !(await save()).error;
  } catch {
    return false;
  }
}

/** Optional prompts only. No severity is inferred, no save or closure gate is added. */
export const MEDICATION_SUBTYPE_PROMPTS: Record<MedicationIncidentSubtype, string> = {
  OMISSION: 'If known, add the intended dose, scheduled time and when the omission was found.',
  WRONG_MEDICATION:
    'If known, compare the intended and given medicines and the administration time.',
  WRONG_DOSE:
    'If known, compare the intended and given doses and record the scheduled and actual times.',
  WRONG_STRENGTH: 'If known, compare the intended and given strengths and doses.',
  WRONG_FORM: 'If known, compare the intended and given formulations and routes.',
  WRONG_TIME: 'If known, add scheduled and actual times and the dose given.',
  WRONG_ROUTE: 'If known, compare the intended and actual routes and doses.',
  WRONG_CLIENT:
    'If known, record the medicine and time. An investigator with access to both residents can link the other resident’s eMAR record.',
  EXTRA_DOSE: 'If known, add the intended dose, extra dose and administration times.',
  EXPIRED_MEDICATION:
    'If known, identify the medicine, dose and time it was given; record expiry details in the event account.',
  PATCH_NOT_REMOVED:
    'If known, identify the patch and when it was applied, due for removal and removed in the event account.',
  ADVERSE_REACTION:
    'If known, add the medicine, dose, event time and clinical advice sought or given.',
  ALLERGY_MEDICATION_GIVEN:
    'If known, add the medicine, dose, event time and clinical advice sought or given.',
  PRESCRIBING_ERROR:
    'If known, compare intended and recorded medicine facts and identify the prescribing stage.',
  NO_PRESCRIBER_ORDER:
    'If known, add the medicine, dose, time and advice sought to clarify the order.',
  PHARMACY_DISPENSING_ERROR:
    'If known, compare intended and supplied medicine facts and identify the dispensing stage.',
  DOCUMENTATION_ERROR:
    'If known, add the affected medicine facts, time and staff or shift account.',
  DD_REGISTER_PREVIOUS_ENTRY_INCORRECT:
    'If known, add the witness or checker, medicine and dose; describe the register discrepancy in the event account.',
  DD_TWO_PERSON_CHECK_MISSED: 'If known, add the witness or checker, medicine, dose and time.',
  DD_SECOND_CHECKER_NOT_WITNESSED:
    'If known, add the witness or checker, medicine, dose and actual time.',
  OTHER: 'Add any medicine facts, times, witnesses and advice that are known.',
};
export const MEDICATION_SEVERITY_GUIDANCE =
  'Consider actual or possible harm when reviewing severity. Seek prompt clinical assessment for an immediate concern and follow your provider’s escalation process. These prompts do not decide severity or reportability. All fields remain optional; saving and reporting can continue.';
